import styled from "styled-components";
import AnimatedCountrySelect from "./components/AnimatedCountrySelect";
import React, { ChangeEvent, useCallback, useMemo } from "react";
import {
  IdentificationDocumentData,
  LandingPageProxyPassportFormPassenger,
} from "../../types/IdentificationForm";
import { FormikErrors } from "formik";
import DateField from "./components/DateField";
import { IdType } from "../../enums/IdType";
import { format as formatDate } from "date-fns";
import {
  de,
  el,
  enGB,
  es,
  fi,
  fr,
  it,
  ko,
  nb,
  nl,
  pl,
  sv,
  tr,
} from "date-fns/locale";
import { useConfig } from "../../contexts";
import AnimatedRoundedSelect from "../../components/AnimatedRoundedSelect/AnimatedRoundedSelect";
import Typography from "../../components/Typography";
import RedInfoBox from "../../components/RedInfoBox";
import ErrorMarker from "../../icons/ErrorMarker";
import TextField from "../../components/TextField";
import { useTranslationV3 } from "../../i18nV3/useTranslationV3";

interface Props extends LandingPageProxyPassportFormPassenger {
  setFieldData: (field: string, value: string) => void;
  values: IdentificationDocumentData;
  index: number;
  errors?: FormikErrors<IdentificationDocumentData>;
}

let locales: { [key: string]: Locale } = {
  de,
  el,
  enGB,
  es,
  fi,
  fr,
  it,
  ko,
  nb,
  nl,
  pl,
  sv,
  tr,
};

const SinglePassenger = ({
  id,
  firstName,
  lastName,
  gender,
  dateOfBirth,
  setFieldData,
  values,
  index,
  errors,
}: Props) => {
  const { t } = useTranslationV3();
  const config = useConfig();

  const errorsToShow = useMemo(() => {
    if (!errors) return [];
    return Object.values(errors);
  }, [errors]);

  const setDateValue = useCallback(
    (value: string) => {
      setFieldData(`${index}.dateOfExpiry`, value);
    },
    [index, setFieldData]
  );

  const idTypeOptions = useMemo(() => {
    return [
      { label: t(`tix_ui_id_type_${IdType.Passport}`), value: IdType.Passport },
    ];
  }, [t]);

  return (
    <MainContainer>
      <InfoContainer>
        <InfoBoldText>{`${firstName} ${lastName}`}</InfoBoldText>
        <InfoNormalText>
          {t(`gender_${gender[0].toLowerCase()}`)}
        </InfoNormalText>
        <InfoNormalText>{`${formatDate(new Date(dateOfBirth), "dd MMMM yyyy", {
          locale: locales[config.defaultLang] || locales.enGB,
        })}`}</InfoNormalText>
      </InfoContainer>
      <InputLabel>{t("tix_ui_identification_form_step_1_id_type")}</InputLabel>
      <StyledAnimatedRoundedSelect
        options={idTypeOptions}
        value={values.idType}
        disabled={idTypeOptions.length < 2}
        onChange={(value) => setFieldData(`${index}.idType`, value)}
        placeholder={IdType.Passport}
        hasError={!!errors?.idType}
        distinctPlaceholder
      />
      <InputLabel>
        {t("tix_ui_identification_form_step_1_issue_country")}
      </InputLabel>
      <AnimatedCountrySelect
        value={values.issueCountry}
        onChange={(value) => setFieldData(`${index}.issueCountry`, value)}
        hasError={!!errors?.issueCountry}
      />
      <InputLabel>
        {t("tix_ui_identification_form_step_1_id_number")}
      </InputLabel>
      <RelativeContainer>
        <TextField
          name="id_number"
          value={values.idNumber}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            setFieldData(`${index}.idNumber`, e.target.value)
          }
          placeholder={t(
            "tix_ui_identification_form_step_1_id_number_placeholder"
          )}
          hasError={!!errors?.idNumber}
        />
        {!!errors?.idNumber && (
          <ErrorMarkerContainer>
            <ErrorMarker />
          </ErrorMarkerContainer>
        )}
      </RelativeContainer>
      <InputLabel>
        {t("tix_ui_identification_form_step_1_date_of_expiry")}
      </InputLabel>
      <RelativeContainer>
        <DateField
          value={values.dateOfExpiry}
          hasError={!!errors?.dateOfExpiry}
          onChange={setDateValue}
        />
        {!!errors?.dateOfExpiry && (
          <ErrorMarkerContainer>
            <ErrorMarker />
          </ErrorMarkerContainer>
        )}
      </RelativeContainer>
      <ErrorsContainer>
        {errorsToShow.map((error, index) => (
          <StyledAlert label={t(error)} key={index} />
        ))}
      </ErrorsContainer>
    </MainContainer>
  );
};

const ErrorMarkerContainer = styled.div`
  position: absolute;
  top: 50%;
  right: 0;
  transform: translate(-50%, -50%);
  display: flex;
`;

const RelativeContainer = styled.div`
  position: relative;
`;

const StyledAlert = styled(RedInfoBox)`
  font-family: ${({ theme }) => theme.redesign.regularFont};
  background-color: ${({ theme }) => theme.redesign.defaultBackground};
  &:not(:first-child) {
    margin-top: 20px;
  }
`;

const ErrorsContainer = styled.div`
  width: calc(100% - 40px);
  margin: 20px;
`;

const InputLabel = styled.div`
  margin-top: 20px;
  font-size: 12px;
  line-height: 12px;
  padding-bottom: 5px;
  font-family: ${({ theme }) => theme.redesign.regularFont};
  padding-left: 20px;
`;

const MainContainer = styled.div`
  border-top: 1px solid ${({ theme }) => theme.redesign.defaultColor};
  &:not(:first-child) {
    margin-top: 40px;
  }
`;

const InfoContainer = styled.div`
  background-color: ${({ theme }) => theme.redesign.defaultBackground};
  padding: 20px;
  margin-top: 40px;
`;

const InfoBoldText = styled(Typography).attrs({
  isHeavy: true,
  defaultVariant: "normal",
})`
  line-height: 15px;
  padding-bottom: 5px;
`;

const InfoNormalText = styled(Typography).attrs({
  isHeavy: false,
  defaultVariant: "normal",
})`
  line-height: 15px;
  &:not(:last-child) {
    padding-bottom: 5px;
  }
`;

const StyledAnimatedRoundedSelect = styled(AnimatedRoundedSelect)`
  margin-bottom: 20px;
`;
export default SinglePassenger;
